import React, { useState, useEffect, useRef } from 'react'
import { Popover, Spin, Checkbox, Input, Button } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import Axios from 'axios'
import { useDebounce } from 'Hooks'

const { REACT_APP_OL_API_USERNAME, REACT_APP_OL_API_PASSWORD } = process.env

interface Voucher {
  id: string
  name: string
  status: string
  type: string
  promotion_campaign_name: string
}

interface Props {
  selectedVouchers: string[]
  onVoucherSelect: (vouchers: string[]) => void
  operator?: string
  disabled?: boolean
}

const VoucherSelectInput: React.FC<Props> = ({
  selectedVouchers,
  onVoucherSelect,
  operator,
  disabled,
}) => {
  const { REACT_APP_OL_API_PROMOTION_URL } = process.env
  const [vouchers, setVouchers] = useState<Voucher[]>([])
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearchValue = useDebounce(searchValue, 600)
  const [tempSelected, setTempSelected] = useState<string[]>([])
  const [dropdownVisible, setDropdownVisible] = useState(false)
  const searchInputRef = useRef<any>(null)
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  })

  const fetchVouchers = async (page: number = 1, name: string = '') => {
    setLoading(true)
    try {
      const response = await Axios.get(
        `${REACT_APP_OL_API_PROMOTION_URL}/v1/promotions/externals/kariba/promotions`,
        {
          auth: {
            username: REACT_APP_OL_API_USERNAME || '',
            password: REACT_APP_OL_API_PASSWORD || '',
          },
          params: {
            order: 'created_at',
            direction: 'desc',
            status: 'running,waiting',
            page,
            mode: 'coupon',
            has_coupon_pool: true,
            name,
          },
        }
      )

      if (response.status === 200) {
        if (page === 1) {
          setVouchers(response.data.promotions || [])
        } else {
          setVouchers((prev) => [...prev, ...(response.data.promotions || [])])
        }
        setPagination(response.data.pagination)
      }
    } catch (error) {
      console.error('Error fetching vouchers:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVouchers(1, '')
  }, [])

  useEffect(() => {
    fetchVouchers(1, debouncedSearchValue)
  }, [debouncedSearchValue])

  useEffect(() => {
    if (dropdownVisible) {
      setTempSelected(selectedVouchers)
    }
  }, [dropdownVisible, selectedVouchers])

  const handleSearch = (value: string) => {
    setSearchValue(value)
  }

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { target } = e
    const element = target as HTMLDivElement

    if (element.scrollTop + element.offsetHeight === element.scrollHeight) {
      if (pagination.current_page < pagination.last_page && !loading) {
        fetchVouchers(pagination.current_page + 1, debouncedSearchValue)
      }
    }
  }

  const isSingleSelect = operator === 'EQUAL' || operator === 'NOT_EQUAL'

  const handleCheckboxChange = (voucherId: string, checked: boolean) => {
    if (isSingleSelect) {
      setTempSelected(checked ? [voucherId] : [])
    } else {
      if (checked) {
        setTempSelected([...tempSelected, voucherId])
      } else {
        setTempSelected(tempSelected.filter((id) => id !== voucherId))
      }
    }
  }

  const handleOk = () => {
    onVoucherSelect(tempSelected)
    setDropdownVisible(false)
  }

  const handleClear = () => {
    setTempSelected([])
  }

  const dropdownContent = () => (
    <div style={{ width: '600px' }}>
      <Input
        ref={searchInputRef}
        placeholder="Search..."
        prefix={<SearchOutlined />}
        value={searchValue}
        onChange={(e) => handleSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />

      <div
        style={{
          maxHeight: 400,
          overflowY: 'auto',
          border: '1px solid #f0f0f0',
          borderRadius: 4,
        }}
        onScroll={handlePopupScroll}
      >
        {/* Select All checkbox for multi-select */}
        {!isSingleSelect && vouchers.length > 0 && (
          <div
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: '#fafafa',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Checkbox
              checked={vouchers.length > 0 && tempSelected.length === vouchers.length}
              indeterminate={tempSelected.length > 0 && tempSelected.length < vouchers.length}
              onChange={(e) => {
                if (e.target.checked) {
                  setTempSelected(vouchers.map((v) => v.id))
                } else {
                  setTempSelected([])
                }
              }}
              style={{ marginRight: 8 }}
            />
            <span style={{ fontWeight: 500 }}>Select All</span>
            {tempSelected.length > 0 && (
              <span style={{ marginLeft: 'auto', color: '#8c8c8c', fontSize: '12px' }}>
                {tempSelected.length} selected
              </span>
            )}
          </div>
        )}

        {loading && vouchers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <Spin />
          </div>
        ) : vouchers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No vouchers found</div>
        ) : (
          vouchers.map((voucher) => (
            <div
              key={voucher.id}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'flex-start',
                backgroundColor: tempSelected.includes(voucher.id) ? '#e6f4ff' : '#fff',
                borderBottom: '1px solid #f0f0f0',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!tempSelected.includes(voucher.id)) {
                  e.currentTarget.style.backgroundColor = '#fafafa'
                }
              }}
              onMouseLeave={(e) => {
                if (!tempSelected.includes(voucher.id)) {
                  e.currentTarget.style.backgroundColor = '#fff'
                }
              }}
              onClick={() => {
                handleCheckboxChange(voucher.id, !tempSelected.includes(voucher.id))
              }}
            >
              <Checkbox
                checked={tempSelected.includes(voucher.id)}
                style={{ marginRight: 8, marginTop: 2 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', color: '#333' }}>{voucher.name}</div>
                {voucher.promotion_campaign_name && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {voucher.promotion_campaign_name}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        {loading && vouchers.length > 0 && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin />
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px 0',
          borderTop: '1px solid #f0f0f0',
        }}
      >
        <Button onClick={handleClear}>Clear All</Button>
        <Button type="primary" onClick={handleOk}>
          OK ({tempSelected.length} selected)
        </Button>
      </div>
    </div>
  )

  // Style constants similar to ProductSelectInput
  const STYLES = {
    selectedContainer: {
      base: {
        border: '1px solid #d9d9d9',
        borderRadius: '2px',
        padding: '8px',
        transition: 'all 0.3s',
      },
      enabled: {
        backgroundColor: '#fafafa',
        cursor: 'pointer',
        opacity: 1,
      },
      disabled: {
        backgroundColor: '#f5f5f5',
        cursor: 'not-allowed',
        opacity: 0.6,
      },
      hover: {
        borderColor: '#40a9ff',
        boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
      },
      default: {
        borderColor: '#d9d9d9',
        boxShadow: 'none',
      },
    },
    countText: {
      marginBottom: '8px',
      color: '#8c8c8c',
      fontSize: '12px',
    },
    voucherItem: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '4px',
    },
    voucherName: {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
      fontSize: '12px',
      textAlign: 'left' as const,
    },
    moreText: {
      fontSize: '12px',
      color: '#8c8c8c',
      marginTop: '4px',
    },
  } as const

  // Get voucher details for selected items
  const getSelectedVoucherDetails = () => {
    return selectedVouchers.map((id) => {
      const voucher = vouchers.find((v) => v.id === id)
      return voucher || { id, name: id, promotion_campaign_name: null }
    })
  }

  return (
    <Popover
      content={dropdownContent()}
      trigger={disabled ? '' : 'click'}
      visible={disabled ? false : dropdownVisible}
      onVisibleChange={disabled ? () => {} : setDropdownVisible}
      placement="bottom"
      overlayStyle={{ width: 600 }}
    >
      {selectedVouchers.length > 0 ? (
        <div
          style={{
            ...STYLES.selectedContainer.base,
            ...(disabled ? STYLES.selectedContainer.disabled : STYLES.selectedContainer.enabled),
          }}
          onMouseEnter={(e) => {
            if (!disabled) {
              Object.assign(e.currentTarget.style, STYLES.selectedContainer.hover)
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled) {
              Object.assign(e.currentTarget.style, STYLES.selectedContainer.default)
            }
          }}
        >
          <div style={STYLES.countText}>{selectedVouchers.length} selected</div>
          {getSelectedVoucherDetails()
            .slice(0, 3)
            .map((voucher, index) => (
              <div
                key={voucher.id}
                style={{
                  ...STYLES.voucherItem,
                  marginBottom: index < 2 ? '4px' : 0,
                }}
              >
                <div style={STYLES.voucherName}>{voucher.name}</div>
              </div>
            ))}
          {selectedVouchers.length > 3 && (
            <div style={STYLES.moreText}>and {selectedVouchers.length - 3} more...</div>
          )}
        </div>
      ) : (
        <Input
          placeholder={disabled ? 'Please select operator first' : 'Select voucher...'}
          readOnly
          disabled={disabled}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        />
      )}
    </Popover>
  )
}

export default VoucherSelectInput
